import { useState, useEffect } from 'react';
import api from '../services/api';
import { ShippingOrder } from '../types';
import ScanOutModal from '../components/ScanOutModal';
import PackageSetupModal from '../components/PackageSetupModal';

export default function ReadyToShipPage() {
  const [orders, setOrders] = useState<ShippingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<ShippingOrder | null>(null);
  const [showBookModal, setShowBookModal] = useState(false);
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [scanOutOrderId, setScanOutOrderId] = useState<string | null>(null);
  const [packageSetupOrder, setPackageSetupOrder] = useState<{ id: string; order_number: string } | null>(null);

  // Form states
  const [carrierName, setCarrierName] = useState('');
  const [bolNumber, setBolNumber] = useState('');
  const [proNumber, setProNumber] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [pickupNotes, setPickupNotes] = useState('');

  useEffect(() => {
    loadOrders();
  }, [filter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      const data = await api.getReadyToShipQueue(params);
      setOrders(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const openBookModal = (order: ShippingOrder) => {
    setSelectedOrder(order);
    setCarrierName(order.carrier_name || '');
    setBolNumber(order.bol_number || '');
    setProNumber(order.pro_number || '');
    setShowBookModal(true);
  };

  const openPickupModal = (order: ShippingOrder) => {
    setSelectedOrder(order);
    setPickupDate(order.pickup_date || '');
    setPickupTime(order.pickup_time || '');
    setPickupNotes(order.pickup_notes || '');
    setShowPickupModal(true);
  };

  const handleBookShipment = async () => {
    if (!selectedOrder) return;
    setProcessing(true);
    try {
      await api.bookShipment({
        order_id: selectedOrder.id,
        carrier_name: carrierName,
        bol_number: bolNumber,
        pro_number: proNumber,
      });
      setShowBookModal(false);
      loadOrders();
    } catch (error) {
      console.error('Failed to book shipment:', error);
      alert(error instanceof Error ? error.message : 'Failed to book shipment');
    } finally {
      setProcessing(false);
    }
  };

  const handleSchedulePickup = async () => {
    if (!selectedOrder || !pickupDate) return;
    setProcessing(true);
    try {
      await api.schedulePickup({
        order_id: selectedOrder.id,
        pickup_date: pickupDate,
        pickup_time: pickupTime,
        pickup_notes: pickupNotes,
      });
      setShowPickupModal(false);
      loadOrders();
    } catch (error) {
      console.error('Failed to schedule pickup:', error);
      alert(error instanceof Error ? error.message : 'Failed to schedule pickup');
    } finally {
      setProcessing(false);
    }
  };

  const handleScanOutComplete = async () => {
    if (!scanOutOrderId) return;
    try {
      await api.markShipped({ order_id: scanOutOrderId });
      setScanOutOrderId(null);
      loadOrders();
    } catch (error) {
      console.error('Failed to mark shipped:', error);
      alert(error instanceof Error ? error.message : 'Failed to mark shipped');
      setScanOutOrderId(null);
    }
  };

  const getStatusBadge = (order: ShippingOrder) => {
    if (order.shipped_at) {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-900 text-green-300">Shipped</span>;
    }
    if (order.pickup_date) {
      return <span className="px-2 py-1 text-xs rounded-full bg-orange-900 text-orange-300">Pickup Scheduled</span>;
    }
    if (order.booked_at) {
      return <span className="px-2 py-1 text-xs rounded-full bg-blue-900 text-blue-300">Booked</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-yellow-900 text-yellow-300">Ready</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Ready to Ship</h1>
          <p className="text-gray-400 mt-1">Orders with freight paid, ready for shipping</p>
        </div>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Ready</option>
            <option value="pending">Not Booked</option>
            <option value="booked">Booked</option>
            <option value="pickup_scheduled">Pickup Scheduled</option>
          </select>
          <button
            onClick={loadOrders}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <h3 className="text-lg font-medium text-white mb-2">No Orders Ready</h3>
          <p className="text-gray-400">There are no orders ready to ship at this time.</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Ship To</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Method</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Weight</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-750">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{order.order_number}</span>
                        {(order as any).shortage_hold && (
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-red-900 text-red-300 animate-pulse">PARTS SHORTAGE</span>
                        )}
                      </div>
                      <div className="text-gray-400 text-sm">{order.dealer_company}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-white">{order.ship_to_company}</div>
                      <div className="text-gray-400 text-sm">
                        {order.ship_to_city}, {order.ship_to_state} {order.ship_to_zip}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-300">{order.shipping_method || 'Freight'}</td>
                    <td className="px-4 py-4 text-gray-300">{order.total_weight || '-'} lbs</td>
                    <td className="px-4 py-4">{getStatusBadge(order)}</td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2">
                        {!order.booked_at && (
                          <button
                            onClick={() => openBookModal(order)}
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                          >
                            Book
                          </button>
                        )}
                        {order.booked_at && !order.pickup_date && (
                          <button
                            onClick={() => openPickupModal(order)}
                            className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                          >
                            Schedule
                          </button>
                        )}
                        {order.booked_at && !order.shipped_at && (
                          <button
                            onClick={() => (order as any).shortage_hold
                              ? alert('Cannot ship — parts shortage hold is active. Resolve shortages in Factory Admin first.')
                              : setScanOutOrderId(order.id)}
                            className={`px-3 py-1 text-sm rounded transition-colors flex items-center gap-1 ${
                              (order as any).shortage_hold
                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                            </svg>
                            Scan Out & Ship
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Scan-Out Modal */}
      {scanOutOrderId && (
        <ScanOutModal
          orderId={scanOutOrderId}
          scanType="freight"
          onComplete={handleScanOutComplete}
          onClose={() => setScanOutOrderId(null)}
        />
      )}

      {/* Package Setup Modal */}
      {packageSetupOrder && (
        <PackageSetupModal
          orderId={packageSetupOrder.id}
          orderNumber={packageSetupOrder.order_number}
          onClose={() => setPackageSetupOrder(null)}
          onComplete={() => {
            setPackageSetupOrder(null);
            loadOrders();
          }}
        />
      )}

      {/* Book Shipment Modal */}
      {showBookModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Book Shipment</h3>
            <p className="text-gray-400 mb-4">Order: {selectedOrder.order_number}</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Carrier Name</label>
                <input
                  type="text"
                  value={carrierName}
                  onChange={(e) => setCarrierName(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., FedEx Freight"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">BOL Number</label>
                <input
                  type="text"
                  value={bolNumber}
                  onChange={(e) => setBolNumber(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Bill of Lading number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">PRO Number</label>
                <input
                  type="text"
                  value={proNumber}
                  onChange={(e) => setProNumber(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Carrier tracking/PRO number"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowBookModal(false)}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBookShipment}
                disabled={processing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {processing ? 'Booking...' : 'Book Shipment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Pickup Modal */}
      {showPickupModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Schedule Pickup</h3>
            <p className="text-gray-400 mb-4">Order: {selectedOrder.order_number}</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Pickup Date *</label>
                <input
                  type="date"
                  value={pickupDate}
                  onChange={(e) => setPickupDate(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Pickup Time Window</label>
                <input
                  type="text"
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., 10am - 2pm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Notes</label>
                <textarea
                  value={pickupNotes}
                  onChange={(e) => setPickupNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Special instructions..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowPickupModal(false)}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSchedulePickup}
                disabled={processing || !pickupDate}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
              >
                {processing ? 'Scheduling...' : 'Schedule Pickup'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
